import {
    Directive,
    ElementRef,
    HostListener,
    Input,
    OnDestroy,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

@Directive({
    selector: '[appTooltip]',
    standalone: false,
})
export class TooltipDirective implements OnDestroy {
    @Input() public appTooltip = '';
    @Input() public delay? = 0;
    @Input() public appTooltipMultiline? = false;

    private myPopup?: HTMLDivElement;
    private timer?: ReturnType<typeof setTimeout>;

    public constructor(
        private el: ElementRef,
        private translateService: TranslateService
    ) {}

    @HostListener('mouseenter') public onMouseEnter(): void {
        this.timer = setTimeout(() => {
            this.createTooltipPopup();
        }, this.delay);
    }

    @HostListener('mouseleave') public onMouseLeave(): void {
        if (this.timer) clearTimeout(this.timer);
        if (this.myPopup) {
            this.myPopup.remove();
        }
    }

    public ngOnDestroy(): void {
        if (this.myPopup) {
            this.myPopup.remove();
        }
    }

    private createTooltipPopup(): void {
        if (!this.appTooltip) return;

        this.translateService.get(this.appTooltip).subscribe(tooltipText => {
            const popup = document.createElement('div');
            const arrow = document.createElement('span');
            popup.innerHTML = tooltipText;
            popup.setAttribute('class', 'tooltip-container');
            if (this.appTooltipMultiline) {
                popup.classList.add('tooltip-multiline');
            }
            popup.appendChild(arrow);

            // Append hidden to measure actual dimensions
            popup.style.visibility = 'hidden';
            document.body.appendChild(popup);
            this.myPopup = popup;

            const popupRect = popup.getBoundingClientRect();
            const elRect = this.el.nativeElement.getBoundingClientRect();
            const gap = 8;
            const edgePadding = 8;

            // Vertical: prefer above, fall back to below
            const canFitAbove = elRect.top - gap - popupRect.height >= 0;
            const canFitBelow =
                elRect.bottom + gap + popupRect.height <= window.innerHeight;

            let shouldShowBelow: boolean;
            if (canFitAbove) {
                shouldShowBelow = false;
            } else if (canFitBelow) {
                shouldShowBelow = true;
            } else {
                shouldShowBelow =
                    window.innerHeight - elRect.bottom > elRect.top;
            }

            let top: number;
            if (shouldShowBelow) {
                top = elRect.bottom + gap;
                popup.classList.add('tooltip-bottom');
            } else {
                top = elRect.top - gap - popupRect.height;
                popup.classList.add('tooltip-top');
            }

            // Horizontal: center on element, clamp to viewport
            let left = elRect.left + elRect.width / 2 - popupRect.width / 2;
            left = Math.max(
                edgePadding,
                Math.min(
                    left,
                    window.innerWidth - popupRect.width - edgePadding
                )
            );

            // Arrow: point to center of host element
            const arrowLeft = elRect.left + elRect.width / 2 - left;
            arrow.style.left =
                Math.max(10, Math.min(arrowLeft, popupRect.width - 10)) + 'px';

            popup.style.top = top + 'px';
            popup.style.left = left + 'px';
            popup.style.visibility = '';

            setTimeout(() => {
                if (this.myPopup) this.myPopup.remove();
            }, 5000);
        });
    }
}
